import { PaymentMethod } from 'types';

import { hasSingleCreditCardSelection } from './isCreditCardPaymentMethod';

interface ShouldShowBillingAddressFormParams {
  /** Configuração de produto que já hoje liga o formulário de endereço. */
  hasAddressFormOnPayment?: boolean;
  /** Flag da oferta (FIN-7468): a compra exige autenticação 3DS. */
  offerRequiresThreeDs?: boolean;
  selectedPaymentMethodType?: PaymentMethod | string | null;
  /**
   * Seleção completa do comprador. O 3DS é de cartão único: num combinado a
   * decisão não pode depender de qual método foi clicado primeiro (o
   * `selectedPaymentMethodType` é o `[0]` do array de seleção).
   */
  selectedPaymentMethods?:
    | readonly (PaymentMethod | string | null | undefined)[]
    | null;
  /**
   * Modo de dois cartões embutidos: a tela mostra um método só
   * (`credit_card_1`), mas o pagamento tem duas pernas. Sem isto, a tela via
   * "um cartão" e exigia endereço enquanto o handler via "dois cartões" e não
   * enviava o bloco — o comprador preenchia à toa (N-06).
   */
  isEmbeddedTwoCardsMode?: boolean;
  /** Renovação não coleta endereço aqui. */
  isRenewal?: boolean;
}

/**
 * FIN-7470 (T14 / C3DS-09): o provider de 3DS exige endereço de cobrança, e o
 * cartão não o coleta hoje. A regra é **aditiva e restrita ao cartão**:
 *
 *  - sem 3DS na oferta, a decisão é exatamente a de antes — nenhum campo novo
 *    aparece para quem não usa autenticação (AC P1-2.5);
 *  - com 3DS na oferta, só o cartão ganha o formulário. Pix, NuPay e Apple Pay
 *    não passam por autenticação e não podem ganhar um campo obrigatório novo
 *    (AC P1-4.2: "com ou sem 3DS na oferta, nada muda nesses métodos");
 *  - e só o cartão **sozinho**: pagamento combinado está fora do escopo da
 *    feature (AC P1-4.3) e continua exatamente como hoje, qualquer que seja a
 *    ordem em que o comprador clicou os métodos.
 *
 * `hasAddressFormOnPayment` é a configuração de produto que já existia e segue
 * valendo para todos os métodos, sem nenhuma mudança.
 *
 * Boleto fica de fora nos dois casos: ele já tem coleta de endereço própria,
 * dentro do seu método.
 */
export const shouldShowBillingAddressForm = ({
  hasAddressFormOnPayment = false,
  offerRequiresThreeDs = false,
  selectedPaymentMethodType,
  selectedPaymentMethods,
  isEmbeddedTwoCardsMode = false,
  isRenewal = false,
}: ShouldShowBillingAddressFormParams): boolean => {
  if (isRenewal) {
    return false;
  }

  if (!selectedPaymentMethodType) {
    return false;
  }

  if (selectedPaymentMethodType === PaymentMethod.BANKSLIP) {
    return false;
  }

  // Dois cartões embutidos são um combinado disfarçado de método único: fora
  // do escopo do 3DS (AC P1-4.3), exatamente como o handler já decide.
  const selection = isEmbeddedTwoCardsMode
    ? ['credit_card_1', 'credit_card_2']
    : selectedPaymentMethods?.length
      ? selectedPaymentMethods
      : [selectedPaymentMethodType];

  return (
    hasAddressFormOnPayment ||
    (offerRequiresThreeDs && hasSingleCreditCardSelection(selection))
  );
};
